import React, { useState, useEffect } from 'react';
import { mockEmployees, generateMockWeeklySchedules, generateMockMetrics, generateMockMonthlySchedules, getCurrentWeekDates } from './mockData';
import { WeeklySchedule, PlanningMetrics, EmployeeStatusEnum, TimeSlot } from './types';
import { calculateWeeklyHours } from './utils';
import { 
    PlanningMetricsComponent, 
    WeekNavigation, 
    PlanningTable,
    ViewToggle,
    MonthNavigation,
    MonthlyCalendar
} from './components';
import { SettingPageWrapper } from '@connectedComponents';
import { NavigationPathsEnum } from '@utilities/enums';

export const PlanningSettings: React.FC = () => {
    const [viewMode, setViewMode] = useState<'week' | 'month'>('week');
    const [currentWeekStart, setCurrentWeekStart] = useState<Date>(() => {
        const today = new Date();
        const weekDates = getCurrentWeekDates(today);
        return weekDates[0]; // Monday of current week
    });
    const [currentMonth, setCurrentMonth] = useState<Date>(() => new Date());
    
    const [schedules, setSchedules] = useState<WeeklySchedule[]>([]);
    const [metrics, setMetrics] = useState<PlanningMetrics | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    // Load data when week or month changes
    useEffect(() => {
        const loadData = async () => {
            setIsLoading(true);
            try {
                // Simulate API call delay
                await new Promise(resolve => setTimeout(resolve, 500));
                
                let newSchedules: WeeklySchedule[];
                if (viewMode === 'week') {
                    newSchedules = generateMockWeeklySchedules(currentWeekStart);
                } else {
                    newSchedules = generateMockMonthlySchedules(currentMonth);
                }
                
                const newMetrics = generateMockMetrics(newSchedules);
                
                setSchedules(newSchedules);
                setMetrics(newMetrics);
            } catch (error) {
                console.error('Error loading data:', error);
            } finally {
                setIsLoading(false);
            }
        };

        loadData();
    }, [currentWeekStart, currentMonth, viewMode]);

    const handlePreviousWeek = () => {
        const newWeekStart = new Date(currentWeekStart);
        newWeekStart.setDate(currentWeekStart.getDate() - 7);
        setCurrentWeekStart(newWeekStart);
    };

    const handleNextWeek = () => {
        const newWeekStart = new Date(currentWeekStart);
        newWeekStart.setDate(currentWeekStart.getDate() + 7);
        setCurrentWeekStart(newWeekStart);
    };

    const handleCurrentWeek = () => {
        const today = new Date();
        const weekDates = getCurrentWeekDates(today);
        setCurrentWeekStart(weekDates[0]);
    };

    // Month navigation handlers
    const handlePreviousMonth = () => {
        const newMonth = new Date(currentMonth);
        newMonth.setMonth(currentMonth.getMonth() - 1);
        setCurrentMonth(newMonth);
    };

    const handleNextMonth = () => {
        const newMonth = new Date(currentMonth);
        newMonth.setMonth(currentMonth.getMonth() + 1);
        setCurrentMonth(newMonth);
    };

    const handleCurrentMonth = () => {
        setCurrentMonth(new Date());
    };

    const handleViewChange = (view: 'week' | 'month') => {
        setViewMode(view);
    };

    const handleScheduleUpdate = (
        employeeId: string, 
        date: string, 
        status: EmployeeStatusEnum, 
        timeSlots: TimeSlot[]
    ) => {
        setSchedules(prev => {
            return prev.map(schedule => {
                if (schedule.employeeId === employeeId) {
                    return {
                        ...schedule,
                        days: {
                            ...schedule.days,
                            [date]: {
                                ...schedule.days[date],
                                status,
                                timeSlots: status === EmployeeStatusEnum.PRESENT ? timeSlots : [],
                                date: new Date(date)
                            }
                        }
                    };
                }
                return schedule;
            });
        });

        // Recalculate metrics with updated schedules
        setTimeout(() => {
            const updatedSchedules = schedules.map(schedule => {
                if (schedule.employeeId === employeeId) {
                    return {
                        ...schedule,
                        days: {
                            ...schedule.days,
                            [date]: {
                                ...schedule.days[date],
                                status,
                                timeSlots: status === EmployeeStatusEnum.PRESENT ? timeSlots : [],
                                date: new Date(date)
                            }
                        }
                    };
                }
                return schedule;
            });
            const updatedMetrics = generateMockMetrics(updatedSchedules);
            setMetrics(updatedMetrics);
        }, 100);
    };

    if (isLoading) {
        return (
            <SettingPageWrapper activeTab={NavigationPathsEnum.SETTINGS_PLANNING}>
                <div className="flex items-center justify-center min-h-[400px]">
                    <div className="flex items-center space-x-2">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        <span className="text-gray-600">Chargement du planning...</span>
                    </div>
                </div>
            </SettingPageWrapper>
        );
    }

    return (
        <SettingPageWrapper
            activeTab={NavigationPathsEnum.SETTINGS_PLANNING}
        >
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 mb-2">
                            Gestion du Planning
                        </h1>
                        <p className="text-gray-600">
                            Gérez les horaires et la présence de vos employés
                        </p>
                    </div>
                    
                    {/* View Toggle */}
                    <ViewToggle 
                        currentView={viewMode} 
                        onViewChange={handleViewChange} 
                    />
                </div>

                {/* Métriques */}
                {metrics && (
                    <PlanningMetricsComponent metrics={metrics} />
                )}

                {/* Légende des statuts */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                    <h3 className="text-sm font-medium text-gray-900 mb-3">Légende</h3>
                    <div className="flex flex-wrap gap-4">
                        <div className="flex items-center">
                            <div className="w-4 h-4 bg-gray-50 border border-gray-200 border-dashed rounded mr-2"></div>
                            <span className="text-sm text-gray-700">Non défini</span>
                        </div>
                        <div className="flex items-center">
                            <div className="w-4 h-4 bg-green-100 border border-green-200 rounded mr-2"></div>
                            <span className="text-sm text-gray-700">Présent</span>
                        </div>
                        <div className="flex items-center">
                            <div className="w-4 h-4 bg-red-100 border border-red-200 rounded mr-2"></div>
                            <span className="text-sm text-gray-700">Absent</span>
                        </div>
                        <div className="flex items-center">
                            <div className="w-4 h-4 bg-blue-100 border border-blue-200 rounded mr-2"></div>
                            <span className="text-sm text-gray-700">En congé</span>
                        </div>
                        <div className="flex items-center">
                            <div className="w-4 h-4 bg-purple-100 border border-purple-200 rounded mr-2"></div>
                            <span className="text-sm text-gray-700">Jour férié</span>
                        </div>
                        <div className="flex items-center">
                            <div className="w-4 h-4 bg-gray-100 border border-gray-200 rounded mr-2"></div>
                            <span className="text-sm text-gray-700">Boutique fermée</span>
                        </div>
                    </div>
                    <div className="mt-3 text-xs text-gray-500">
                        💡 {viewMode === 'week' 
                            ? 'Cliquez sur une cellule pour changer le statut ou modifier les horaires' 
                            : 'Survolez un jour pour voir les détails, cliquez pour naviguer'}
                    </div>
                </div>

                {/* Content based on view mode */}
                {viewMode === 'week' ? (
                    <>
                        {/* Navigation de semaine */}
                        <WeekNavigation
                            currentWeekStart={currentWeekStart}
                            onPreviousWeek={handlePreviousWeek}
                            onNextWeek={handleNextWeek}
                            onCurrentWeek={handleCurrentWeek}
                        />

                        {/* Tableau de planning */}
                        <PlanningTable
                            employees={mockEmployees}
                            schedules={schedules}
                            weekStartDate={currentWeekStart}
                            onScheduleUpdate={handleScheduleUpdate}
                        />
                    </>
                ) : (
                    <>
                        {/* Navigation mensuelle */}
                        <MonthNavigation
                            currentMonth={currentMonth}
                            onPreviousMonth={handlePreviousMonth}
                            onNextMonth={handleNextMonth}
                            onCurrentMonth={handleCurrentMonth}
                        />

                        {/* Calendrier mensuel */}
                        <MonthlyCalendar
                            currentMonth={currentMonth}
                            employees={mockEmployees}
                            schedules={schedules}
                            onScheduleUpdate={handleScheduleUpdate}
                        />
                    </>
                )}
            </div>
        </SettingPageWrapper>
    );
};